
import React, {
  useEffect,
  useMemo,
  useState,
} from 'react';
import {
  useNavigate,
  useParams,
} from 'react-router-dom';
import {
  CalendarDays,
  ChevronLeft,
  ExternalLink,
  FileText,
  GraduationCap,
  HeartPulse,
  Loader2,
  Mail,
  MapPin,
  Pencil,
  Phone,
  ShieldCheck,
  UserRound,
} from 'lucide-react';

import { useSchool } from '../../../context/SchoolContext';

import {
  getApiErrorMessage,
  getDocumentName,
  getDocumentUrl,
  resolveMediaUrl,
  studentApi,
} from '../services/studentApi';

const showValue = (value) =>
  value !== undefined &&
  value !== null &&
  String(value).trim() !== ''
    ? value
    : '—';

const formatDate = (value) => {
  if (!value) {
    return '—';
  }

  const date = new Date(
    `${value}T00:00:00`,
  );

  if (
    Number.isNaN(date.getTime())
  ) {
    return '—';
  }

  return date.toLocaleDateString(
    'en-IN',
    {
      day: '2-digit',
      month: 'short',
      year: 'numeric',
    },
  );
};

const getInitials = (name) => {
  const parts = String(name || '')
    .trim()
    .split(/\s+/)
    .filter(Boolean);

  if (parts.length === 0) {
    return 'ST';
  }

  if (parts.length === 1) {
    return parts[0]
      .slice(0, 2)
      .toUpperCase();
  }

  return `${parts[0][0]}${
    parts[parts.length - 1][0]
  }`.toUpperCase();
};

const StudentAvatar = ({
  photo,
  name,
}) => {
  const [imageFailed, setImageFailed] =
    useState(false);

  const photoUrl =
    resolveMediaUrl(photo);

  useEffect(() => {
    setImageFailed(false);
  }, [photoUrl]);

  return (
    <div className="flex h-24 w-24 shrink-0 items-center justify-center overflow-hidden rounded-full border-4 border-white bg-blue-100 text-2xl font-bold text-blue-700 shadow">
      {photoUrl && !imageFailed ? (
        <img
          src={photoUrl}
          alt={name}
          className="h-full w-full object-cover"
          onError={() =>
            setImageFailed(true)
          }
        />
      ) : (
        getInitials(name)
      )}
    </div>
  );
};

const InfoRow = ({
  label,
  value,
}) => (
  <div className="flex flex-col border-b border-gray-100 py-3 last:border-b-0 sm:flex-row sm:items-start sm:justify-between sm:gap-5">
    <span className="text-sm text-gray-500">
      {label}
    </span>

    <span className="mt-1 break-words text-sm font-medium text-gray-900 sm:mt-0 sm:text-right">
      {showValue(value)}
    </span>
  </div>
);

const InfoCard = ({
  title,
  icon: Icon,
  children,
  className = '',
}) => (
  <section
    className={`rounded-2xl border border-gray-200 bg-white p-6 shadow-sm ${className}`}
  >
    <div className="mb-5 flex items-center gap-3">
      <div className="rounded-xl bg-blue-50 p-2.5 text-blue-600">
        <Icon className="h-5 w-5" />
      </div>

      <h2 className="text-lg font-bold text-gray-900">
        {title}
      </h2>
    </div>

    {children}
  </section>
);

const AddressBlock = ({
  title,
  address,
}) => (
  <div className="rounded-xl border border-gray-200 bg-gray-50 p-4">
    <h3 className="font-semibold text-gray-900">
      {title}
    </h3>

    <div className="mt-3 space-y-1 text-sm leading-6 text-gray-600">
      <p>
        {showValue(
          address?.address_line1,
        )}
      </p>

      {address?.address_line2 && (
        <p>
          {address.address_line2}
        </p>
      )}

      <p>
        {[
          address?.city,
          address?.state,
        ]
          .filter(Boolean)
          .join(', ') || '—'}
      </p>

      <p>
        {[
          address?.country,
          address?.pincode,
        ]
          .filter(Boolean)
          .join(' - ') || '—'}
      </p>
    </div>
  </div>
);

const DOCUMENT_LABELS = {
  BIRTH_CERTIFICATE:
    'Birth Certificate',
  TRANSFER_CERTIFICATE:
    'Transfer Certificate',
  ID_PROOF: 'ID Proof',
};

const StudentDetailPage = () => {
  const { id } = useParams();
  const { schoolId } = useSchool();
  const navigate = useNavigate();

  const [student, setStudent] =
    useState(null);

  const [loading, setLoading] =
    useState(true);

  const [error, setError] =
    useState('');

  useEffect(() => {
    let cancelled = false;

    const loadStudent = async () => {
      if (!schoolId || !id) {
        setLoading(false);
        return;
      }

      setLoading(true);
      setError('');

      try {
        const response =
          await studentApi.getStudentById(
            schoolId,
            id,
          );

        if (!cancelled) {
          setStudent(
            response?.data ?? response,
          );
        }
      } catch (requestError) {
        if (!cancelled) {
          setError(
            getApiErrorMessage(
              requestError,
              'Unable to load student details.',
            ),
          );
        }
      } finally {
        if (!cancelled) {
          setLoading(false);
        }
      }
    };

    loadStudent();

    return () => {
      cancelled = true;
    };
  }, [schoolId, id]);

  const academicProfile = useMemo(() => {
    const profiles = Array.isArray(
      student?.academic_profiles,
    )
      ? student.academic_profiles
      : [];

    return (
      profiles.find(
        (profile) =>
          profile.status === 'ACTIVE',
      ) ||
      student?.current_academic ||
      profiles[0] ||
      {}
    );
  }, [student]);

  const currentAddress = useMemo(
    () =>
      student?.addresses?.find(
        (address) =>
          address.address_type ===
          'CURRENT',
      ) || null,
    [student],
  );

  const permanentAddress = useMemo(
    () =>
      student?.addresses?.find(
        (address) =>
          address.address_type ===
          'PERMANENT',
      ) || null,
    [student],
  );

  if (loading) {
    return (
      <div className="flex min-h-[60vh] flex-col items-center justify-center">
        <Loader2 className="h-9 w-9 animate-spin text-blue-600" />

        <p className="mt-3 text-sm text-gray-500">
          Loading student profile...
        </p>
      </div>
    );
  }

  if (error || !student) {
    return (
      <div className="rounded-xl border border-red-200 bg-red-50 p-6 text-center text-red-700">
        {error ||
          'Student was not found.'}
      </div>
    );
  }

  const fullName =
    student.full_name ||
    [
      student.first_name,
      student.middle_name,
      student.last_name,
    ]
      .filter(Boolean)
      .join(' ');

  const parent =
    student.parent_detail || {};

  const medical =
    student.medical_detail || {};

  const previousSchool =
    student.previous_school_detail ||
    {};

  const documents = Array.isArray(
    student.documents,
  )
    ? student.documents
    : [];

  return (
    <div className="mx-auto max-w-7xl pb-10">
      <div className="mb-5 flex flex-wrap items-center justify-between gap-3">
        <button
          type="button"
          onClick={() =>
            navigate('/students')
          }
          className="inline-flex items-center gap-2 text-sm font-medium text-gray-600 hover:text-blue-600"
        >
          <ChevronLeft className="h-4 w-4" />
          Back to students
        </button>

        <button
          type="button"
          onClick={() =>
            navigate(
              `/students/${id}/edit`,
            )
          }
          className="inline-flex items-center gap-2 rounded-lg bg-blue-600 px-4 py-2.5 text-sm font-semibold text-white hover:bg-blue-700"
        >
          <Pencil className="h-4 w-4" />
          Edit Student
        </button>
      </div>

<section className="mb-6 rounded-2xl bg-white p-7 text-slate-900 shadow-sm">
        <div className="flex flex-col gap-5 md:flex-row md:items-center">
          <StudentAvatar
            photo={student.photo}
            name={fullName}
          />

          <div className="min-w-0 flex-1">
            <div className="flex flex-wrap items-center gap-3">
              <h1 className="text-3xl font-bold">
                {fullName}
              </h1>

              <span
                className={`rounded-full px-3 py-1 text-xs font-semibold ${
                  student.status === 'ACTIVE'
                    ? 'bg-green-100 text-green-700'
                    : 'bg-gray-100 text-gray-700'
                }`}
              >
                {student.status}
              </span>
            </div>

            <div className="mt-3 flex flex-wrap gap-x-6 gap-y-2 text-sm text-slate-900">
              <span>
                Enrolment:{' '}
                {showValue(
                  student.enrolment_no,
                )}
              </span>

              <span>
                Admission:{' '}
                {showValue(
                  student.admission_no,
                )}
              </span>

              <span>
                {showValue(
                  academicProfile.class_name,
                )}
                {' - '}
                {showValue(
                  academicProfile.section_name,
                )}
              </span>
            </div>
          </div>
        </div>
      </section>

      <div className="grid grid-cols-1 gap-6 xl:grid-cols-2">
        <InfoCard
          title="Personal Information"
          icon={UserRound}
        >
          <InfoRow
            label="First Name"
            value={student.first_name}
          />

          <InfoRow
            label="Middle Name"
            value={student.middle_name}
          />

          <InfoRow
            label="Last Name"
            value={student.last_name}
          />

          <InfoRow
            label="Gender"
            value={student.gender}
          />

          <InfoRow
            label="Date of Birth"
            value={formatDate(
              student.date_of_birth,
            )}
          />

          <InfoRow
            label="Blood Group"
            value={student.blood_group}
          />

          <InfoRow
            label="Nationality"
            value={student.nationality}
          />

          <InfoRow
            label="Religion"
            value={student.religion}
          />

          <InfoRow
            label="Caste Category"
            value={student.caste_category}
          />

          <InfoRow
            label="Aadhaar Number"
            value={student.aadhaar_no}
          />

          <InfoRow
            label="Admission Date"
            value={formatDate(
              student.admission_date,
            )}
          />
        </InfoCard>

        <InfoCard
          title="Academic Information"
          icon={GraduationCap}
        >
          <InfoRow
            label="Academic Year"
            value={
              academicProfile
                .academic_year_name
            }
          />

          <InfoRow
            label="Class"
            value={
              academicProfile.class_name
            }
          />

          <InfoRow
            label="Section"
            value={
              academicProfile.section_name
            }
          />

          <InfoRow
            label="Roll Number"
            value={
              academicProfile.roll_no
            }
          />

          <InfoRow
            label="Academic Status"
            value={
              academicProfile.status
            }
          />

          <InfoRow
            label="Student Status"
            value={student.status}
          />
        </InfoCard>

        <InfoCard
          title="Parent and Guardian Details"
          icon={ShieldCheck}
          className="xl:col-span-2"
        >
          <div className="grid grid-cols-1 gap-6 lg:grid-cols-3">
            <div className="rounded-xl border border-gray-200 p-5">
              <h3 className="font-semibold text-gray-900">
                Father's Information
              </h3>

              <div className="mt-3">
                <InfoRow
                  label="Name"
                  value={
                    parent.father_name
                  }
                />

                <InfoRow
                  label="Occupation"
                  value={
                    parent.father_occupation
                  }
                />

                <InfoRow
                  label="Phone"
                  value={
                    parent.father_phone
                  }
                />

                <InfoRow
                  label="Email"
                  value={
                    parent.father_email
                  }
                />
              </div>
            </div>

            <div className="rounded-xl border border-gray-200 p-5">
              <h3 className="font-semibold text-gray-900">
                Mother's Information
              </h3>

              <div className="mt-3">
                <InfoRow
                  label="Name"
                  value={
                    parent.mother_name
                  }
                />

                <InfoRow
                  label="Occupation"
                  value={
                    parent.mother_occupation
                  }
                />

                <InfoRow
                  label="Phone"
                  value={
                    parent.mother_phone
                  }
                />

                <InfoRow
                  label="Email"
                  value={
                    parent.mother_email
                  }
                />
              </div>
            </div>

            <div className="rounded-xl border border-gray-200 p-5">
              <h3 className="font-semibold text-gray-900">
                Primary Contact
              </h3>

              <div className="mt-3">
                <InfoRow
                  label="Name"
                  value={
                    parent
                      .primary_contact_name
                  }
                />

                <InfoRow
                  label="Phone"
                  value={
                    parent
                      .primary_contact_phone
                  }
                />
              </div>
            </div>
          </div>
        </InfoCard>

        <InfoCard
          title="Address Details"
          icon={MapPin}
          className="xl:col-span-2"
        >
          <div className="grid grid-cols-1 gap-5 md:grid-cols-2">
            <AddressBlock
              title="Current Address"
              address={currentAddress}
            />

            <AddressBlock
              title="Permanent Address"
              address={permanentAddress}
            />
          </div>
        </InfoCard>

        <InfoCard
          title="Medical Details"
          icon={HeartPulse}
        >
          <InfoRow
            label="Allergies"
            value={medical.allergies}
          />

          <InfoRow
            label="Medical Conditions"
            value={
              medical.medical_conditions
            }
          />

          <InfoRow
            label="Regular Medication"
            value={
              medical.regular_medication
            }
          />

          <InfoRow
            label="Doctor Name"
            value={medical.doctor_name}
          />

          <InfoRow
            label="Doctor Phone"
            value={
              medical.doctor_phone
            }
          />

          <InfoRow
            label="Emergency Instructions"
            value={
              medical
                .emergency_instructions
            }
          />
        </InfoCard>

        <InfoCard
          title="Previous School"
          icon={CalendarDays}
        >
          <InfoRow
            label="School Name"
            value={
              previousSchool
                .previous_school_name
            }
          />

          <InfoRow
            label="Previous Class"
            value={
              previousSchool
                .previous_class
            }
          />

          <InfoRow
            label="Transfer Certificate"
            value={
              previousSchool
                .transfer_certificate_no
            }
          />

          <InfoRow
            label="Last Attended Date"
            value={formatDate(
              previousSchool
                .last_attended_date,
            )}
          />

          <InfoRow
            label="Reason for Leaving"
            value={
              previousSchool
                .reason_for_leaving
            }
          />
        </InfoCard>

        <InfoCard
          title="Documents"
          icon={FileText}
          className="xl:col-span-2"
        >
          {documents.length === 0 ? (
            <p className="rounded-xl bg-gray-50 p-5 text-sm text-gray-500">
              No documents have been uploaded.
            </p>
          ) : (
            <div className="grid grid-cols-1 gap-4 md:grid-cols-2">
              {documents.map(
                (document) => {
                  const documentUrl =
                    getDocumentUrl(document);

                  const documentName =
                    getDocumentName(document);

                  return (
                    <div
                      key={document.id}
                      className="flex items-start gap-3 rounded-xl border border-gray-200 p-4"
                    >
                      <div className="rounded-lg bg-blue-50 p-2.5 text-blue-600">
                        <FileText className="h-5 w-5" />
                      </div>

                      <div className="min-w-0 flex-1">
                        <p className="font-semibold text-gray-900">
                          {DOCUMENT_LABELS[
                            document
                              .document_type
                          ] ||
                            document.document_type ||
                            'Document'}
                        </p>

                        <p
                          title={
                            documentName
                          }
                          className="mt-1 truncate text-xs text-gray-500"
                        >
                          {documentName}
                        </p>

                        {document.document_number && (
                          <p className="mt-1 text-xs text-gray-500">
                            Number:{' '}
                            {
                              document.document_number
                            }
                          </p>
                        )}

                        {documentUrl ? (
                          <a
                            href={
                              documentUrl
                            }
                            target="_blank"
                            rel="noreferrer"
                            className="mt-3 inline-flex items-center gap-1 text-sm font-medium text-blue-600 hover:text-blue-700"
                          >
                            View document
                            <ExternalLink className="h-3.5 w-3.5" />
                          </a>
                        ) : (
                          <p className="mt-3 text-xs text-red-500">
                            File URL is not
                            available.
                          </p>
                        )}
                      </div>
                    </div>
                  );
                },
              )}
            </div>
          )}
        </InfoCard>
      </div>
    </div>
  );
};

export default StudentDetailPage;
